/**
 * A help article's picture (TMMAPP-0142 §I, K1). Help images need the bearer token, so the served
 * path (used as is, never composed) is read through the API client as a blob when the picture
 * scrolls into view (`useLazyObjectUrl`, the transcript's loader) and dropped when it leaves. Only a
 * path under `/v1/me/help/images/` is ever fetched; any other picture, a missing one
 * (`HELP_IMAGE_NOT_FOUND`) or any failure shows its alt text. The alt text is always the picture's
 * name.
 */
import { Box, Typography, useTheme } from '@mui/material';
import { helpApi, isHelpImagePath } from '@/api/help';
import { useLazyObjectUrl } from '@/shared/useLazyObjectUrl';
import { resolveTokens } from '@/theme/theme';

function AltText({ alt }: { alt: string }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Typography
      component="span"
      data-help-image-alt=""
      sx={{ fontSize: 13, color: tokens.text2, fontStyle: 'italic' }}
    >
      {alt}
    </Typography>
  );
}

function LoadedImage({ alt, src }: { alt: string; src: string }) {
  const tokens = resolveTokens(useTheme());
  const { observe, url, failed } = useLazyObjectUrl((signal) => helpApi.image(src, signal));
  if (failed) return <AltText alt={alt} />;
  return (
    <Box
      component="span"
      ref={observe}
      data-help-image={src}
      sx={{
        display: 'block',
        maxWidth: '100%',
        my: 0.5,
        borderRadius: 1,
      }}
    >
      {url === null && <AltText alt={alt} />}
      {url !== null && (
        <Box
          component="img"
          src={url}
          alt={alt}
          sx={{
            display: 'block',
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
            border: `1px solid ${tokens.line}`,
          }}
        />
      )}
    </Box>
  );
}

export function HelpImage({ alt, src }: { alt: string; src: string }) {
  if (!isHelpImagePath(src)) return <AltText alt={alt} />;
  return <LoadedImage alt={alt} src={src} />;
}
